import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from '../../hooks/useTranslation';
import { infoPages } from '../../config/infoPages';
import WidthWrapperEx from '../layout/WidthWrapperEx';
import DetailIcon from '../ui/DetailIcon';

function InfoNavigation({ label, pathname, t }) {
  return <nav aria-label={label}>
    <ul className="space-y-1">
      {Object.entries(infoPages).map(([href, key]) => <li key={href}>
        <Link href={href} prefetch={false} aria-current={pathname === href ? 'page' : undefined}
          className={`flex min-h-[44px] items-center justify-between gap-3 rounded-lg px-3 py-3 text-[13px] font-bold leading-snug focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700 ${pathname === href ? 'bg-krit-surface text-white' : 'text-krit-surface hover:bg-white'}`}>
          {t(key)}
          {pathname === href && <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />}
        </Link>
      </li>)}
    </ul>
  </nav>;
}

export default function InfoPage({ title, children }) {
  const { pathname } = useRouter();
  const { t, locale } = useTranslation('breadcrumbs');
  const label = locale === 'en' ? 'Useful information' : 'Informații utile';
  const navigation = <InfoNavigation label={label} pathname={pathname} t={t} />;

  return <WidthWrapperEx size="detail" fullHeight={false} className="px-4 py-7 text-krit-surface sm:px-6 sm:py-10">
    <header className="mb-6 sm:mb-8">
      <h1 className="max-w-[900px] break-words text-[30px] font-black leading-[1.12] tracking-tight sm:text-[42px]">
        {title}<span className="text-teal-500">.</span>
      </h1>
    </header>
    <div className="grid items-start gap-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-9">
      <aside className="min-w-0 lg:sticky lg:top-24">
        <div className="hidden rounded-xl bg-krit-paper p-3 lg:block">
          <p className="px-3 pb-3 pt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-krit-muted">{label}</p>
          {navigation}
        </div>
        <details className="group rounded-xl bg-krit-paper lg:hidden">
          <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-3 rounded-xl px-4 py-3 text-[13px] font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-700 [&::-webkit-details-marker]:hidden">
            {label}
            <DetailIcon name="chevronRight" filled className="h-4 w-4 rotate-90 group-open:-rotate-90" />
          </summary>
          <div className="px-3 pb-3">{navigation}</div>
        </details>
      </aside>
      <div className="min-w-0 space-y-5">{children}</div>
    </div>
  </WidthWrapperEx>;
}
